import { zodResolver } from "@hookform/resolvers/zod";
import classNames from "classnames";
import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef } from "react";
import { useWatch, type UseFormReturn } from "react-hook-form";
import { z } from "zod";

import { useAccessControl } from "../../../app/access-control-context";
import { states } from "../../../data/states";
import type { BasicFormProps } from "../../../forms/form-context-wrapper/form-context-wrapper";
import { useCityAndState } from "../../../hooks/use-azure-maps-city-state";
import { useEmploymentHistory } from "../../../hooks/use-employment-history";
import { useStaffMember, useUpdateStaffMember } from "../../../hooks/use-user";
import { CheckboxFieldLabelLast } from "../../forms/checkbox-field";
import {
  isValidOptionalEmployeeNumber,
  isValidOptionalPhoneNumber,
  isValidZipcode,
  zipcodeSchema
} from "../../forms/form-helpers";
import { FormSection } from "../../forms/form-section";
import { InputField } from "../../forms/input-field";
import { PhoneNumberField } from "../../forms/phone-number-field";
import { SelectField } from "../../forms/select-field";
import { TextAreaField } from "../../forms/text-area-field";
import { LoadingButton } from "../../loading-button/loading-button";
import { LoadingOverlay } from "../../loading-overlay/loading-overlay";
import { useFormModal } from "../../modal/modal";
import { Confirm, ConfirmHandle } from "../../confirm/confirm";
import * as Permissions from "../../../data/permissions";

const STAFF_BIO_LIMITS = {
  graduatedFrom: 30,
  degree: 75,
  areaOfStudy: 75,
  certificationsEarned: 75,
  volunteersFor: 100,
  chamberMembership: 100,
  whyHlc: 200,
  hobbies: 100,
  aboutTheOwner: 200
} as const;

type StaffBioLimitedField = keyof typeof STAFF_BIO_LIMITS;

const CharacterCount = ({ name, value }: { name: StaffBioLimitedField; value?: string }) => (
  <div className="form-text text-end" data-testid={`character-count-${name}`}>
    {`${value?.length ?? 0}/${STAFF_BIO_LIMITS[name]}`}
  </div>
);

export interface EditStaffContactFormRef {
  isDirty: boolean | undefined;
}
interface EditContactFormProps extends BasicFormProps<EditContactFormValues> {
  showEmployeeNumber?: boolean;
  showAboutTheOwner?: boolean;
  businessEmail?: string;
  isLoading?: boolean;
}

export const EditStaffContactForm = forwardRef<EditStaffContactFormRef, EditContactFormProps>(
  ({ showEmployeeNumber = false, showAboutTheOwner = false, methods, isLoading, businessEmail }, ref) => {
    const { hasFeatureAccess, hasPermission } = useAccessControl();
    const canSeeProfileFields = hasFeatureAccess?.("staff_profile") && hasPermission?.(Permissions.STAFF_VIEW_BIO);
    const canEditProfileFields = canSeeProfileFields && hasPermission?.(Permissions.STAFF_EDIT_BIO);

    useImperativeHandle(ref, () => ({
      isDirty: methods?.formState.isDirty
    }));

    const {
      register,
      setValue,
      watch,
      control,
      formState: { errors }
    } = methods ??
    ({
      formState: {},
      trigger: () => {
        return;
      }
    } as UseFormReturn<EditContactFormValues>);
    const staffProfileValues = useWatch({ control, name: "staffProfile" });
    const zipCodeFieldValue = useWatch({ control, name: "zipcode", defaultValue: "" });
    const cityFieldValue = useWatch({ control, name: "city", defaultValue: "" });
    const stateFieldValue = useWatch({ control, name: "state", defaultValue: "" });
    const cityAndStateQuery = useCityAndState(zipCodeFieldValue, {
      enabled: isValidZipcode(zipCodeFieldValue) && (!cityFieldValue || !stateFieldValue)
    });

    const onChangePublished = useCallback(() => {
      methods?.trigger("staffProfile");
    }, [methods?.trigger]);

    const onChangePhoneNumber = useCallback(() => {
      methods?.trigger("phoneNumbers");
    }, [methods?.trigger]);

    useEffect(() => {
      if (cityAndStateQuery?.data?.city && cityAndStateQuery?.data?.state && setValue) {
        setValue("city", cityAndStateQuery.data.city);
        setValue("state", cityAndStateQuery.data.state);
      }
    }, [cityAndStateQuery.data]);

    const isProfilePublished = watch("staffProfile.isProfilePublished");

    return (
      <LoadingOverlay loading={isLoading}>
        <div className="form" data-testid="staff-details-tab-content-contact">
          <FormSection>
            <div className="col col-12 col-md-6">
              <InputField
                label="First Name"
                placeholder="First Name"
                isRequired
                error={errors.firstName}
                disableColClass
                {...register("firstName")}
              />
            </div>
            <div className="col col-12 col-md-6">
              <InputField
                label="Last Name"
                placeholder="Last Name"
                isRequired
                error={errors.lastName}
                disableColClass
                {...register("lastName")}
              />
            </div>
            {showEmployeeNumber && (
              <div className="col col-12 col-md-6">
                <InputField
                  label="HR ID"
                  placeholder="HR ID"
                  error={errors.employeeNumber}
                  disableColClass
                  {...register("employeeNumber")}
                />
              </div>
            )}
            <div className={classNames("col col-12", { "col-md-6": showEmployeeNumber })}>
              <InputField
                label="HLC Account"
                placeholder="HLC Account"
                value={businessEmail ?? defaultStaffMemberContactValues.businessEmail}
                disabled
                readOnly
                disableColClass
              />
            </div>
          </FormSection>

          <h3 className="card-subheader mt-3">Contact</h3>
          <FormSection>
            <div className="col col-12 col-md-6">
              <InputField
                label="Address 1"
                placeholder="Address 1"
                error={errors.line1}
                disableColClass
                {...register("line1")}
              />
            </div>
            <div className="col col-12 col-md-6">
              <InputField
                label="Address 2"
                placeholder="Address 2"
                error={errors.line2}
                disableColClass
                {...register("line2")}
              />
            </div>
            <div className="col col-12 col-md-4">
              <InputField
                label="ZIP"
                placeholder="ZIP"
                error={errors.zipcode}
                disableColClass
                {...register("zipcode")}
              />
            </div>
            <div className="col col-12 col-md-4">
              <InputField label="City" placeholder="City" error={errors.city} disableColClass {...register("city")} />
            </div>
            <div className="col col-12 col-md-4">
              <SelectField label="State" error={errors.state} disableColClass {...register("state")}>
                <option value="">Select a State</option>
                {states.map(({ abbreviation, name }) => (
                  <option key={abbreviation} value={abbreviation}>
                    {name}
                  </option>
                ))}
              </SelectField>
            </div>
            <div className="col col-12 col-md-4">
              <PhoneNumberField
                label="Mobile Phone"
                placeholder="Mobile Phone"
                error={errors.mobilePhone ?? errors.phoneNumbers}
                validationState={errors.phoneNumbers && "error"}
                disableColClass
                {...register("mobilePhone", { onChange: onChangePhoneNumber })}
              />
            </div>
            <div className="col col-12 col-md-4">
              <PhoneNumberField
                label="Home Phone"
                placeholder="Home Phone"
                error={errors.homePhone}
                validationState={errors.phoneNumbers && "error"}
                disableColClass
                {...register("homePhone", { onChange: onChangePhoneNumber })}
              />
            </div>
            <div className="col col-12 col-md-4">
              <PhoneNumberField
                label="Other Phone"
                placeholder="Other Phone"
                error={errors.otherPhone}
                validationState={errors.phoneNumbers && "error"}
                disableColClass
                {...register("otherPhone", { onChange: onChangePhoneNumber })}
              />
            </div>
            <div className="col col-12">
              <InputField
                label="Personal Email"
                placeholder="Personal Email"
                isRequired
                error={errors.personalEmail}
                disableColClass
                {...register("personalEmail")}
              />
            </div>
          </FormSection>

          <h3 className="card-subheader mt-3">Emergency Contact</h3>
          <FormSection>
            <div className="col col-12 col-lg-6">
              <InputField
                label="Name"
                placeholder="Name"
                error={errors.emergencyContactName}
                disableColClass
                {...register("emergencyContactName")}
              />
            </div>
            <div className="col col-12 col-lg-6">
              <PhoneNumberField
                label="Phone"
                placeholder="Phone"
                error={errors.emergencyContactPhone}
                disableColClass
                {...register("emergencyContactPhone")}
              />
            </div>
          </FormSection>

          {canSeeProfileFields && (
            <>
              <h3 className="card-subheader mt-3">Staff Bio</h3>
              <FormSection>
                <div className="col col-12">
                  <CheckboxFieldLabelLast
                    label="Publish staff information to your center's HLC website"
                    disabled={!canEditProfileFields}
                    {...register("staffProfile.isProfilePublished", { onChange: onChangePublished })}
                  />
                </div>
                <div className="col col-12 col-lg-6">
                  <InputField
                    label="Graduated From"
                    placeholder="Graduated From"
                    error={errors.staffProfile?.graduatedFrom}
                    validationState={errors.staffProfile?.graduatedFrom && "error"}
                    isRequired={isProfilePublished}
                    disabled={!canEditProfileFields}
                    disableColClass
                    maxLength={STAFF_BIO_LIMITS.graduatedFrom}
                    {...register("staffProfile.graduatedFrom")}
                  />
                  <CharacterCount name="graduatedFrom" value={staffProfileValues?.graduatedFrom} />
                </div>
                <div className="col col-12 col-lg-6">
                  <InputField
                    label="Degree"
                    placeholder="Degree"
                    error={errors.staffProfile?.degree}
                    validationState={errors.staffProfile?.degree && "error"}
                    isRequired={isProfilePublished}
                    disabled={!canEditProfileFields}
                    disableColClass
                    maxLength={STAFF_BIO_LIMITS.degree}
                    {...register("staffProfile.degree")}
                  />
                  <CharacterCount name="degree" value={staffProfileValues?.degree} />
                </div>
                <div className="col col-12 col-lg-6">
                  <InputField
                    label="Area of Study"
                    placeholder="Area of Study"
                    error={errors.staffProfile?.areaOfStudy}
                    validationState={errors.staffProfile?.areaOfStudy && "error"}
                    isRequired={isProfilePublished}
                    disabled={!canEditProfileFields}
                    disableColClass
                    maxLength={STAFF_BIO_LIMITS.areaOfStudy}
                    {...register("staffProfile.areaOfStudy")}
                  />
                  <CharacterCount name="areaOfStudy" value={staffProfileValues?.areaOfStudy} />
                </div>
                <div className="col col-12 col-lg-6">
                  <InputField
                    type="number"
                    step={1}
                    label="Teaching Since"
                    placeholder="Teaching Since"
                    error={errors.staffProfile?.teachingSince}
                    validationState={errors.staffProfile?.teachingSince && "error"}
                    isRequired={isProfilePublished}
                    disabled={!canEditProfileFields}
                    disableColClass
                    {...register("staffProfile.teachingSince")}
                  />
                </div>
                <div className="col col-12">
                  <TextAreaField
                    label="Certifications Earned"
                    placeholder="Certifications Earned"
                    error={errors.staffProfile?.certificationsEarned}
                    validationState={errors.staffProfile?.certificationsEarned && "error"}
                    style={{ minHeight: "100px" }}
                    disabled={!canEditProfileFields}
                    disableColClass
                    maxLength={STAFF_BIO_LIMITS.certificationsEarned}
                    {...register("staffProfile.certificationsEarned")}
                  />
                  <CharacterCount name="certificationsEarned" value={staffProfileValues?.certificationsEarned} />
                </div>
                <div className="col col-12">
                  <TextAreaField
                    label="Volunteers For"
                    placeholder="Volunteers For"
                    error={errors.staffProfile?.volunteersFor}
                    validationState={errors.staffProfile?.volunteersFor && "error"}
                    style={{ minHeight: "100px" }}
                    disabled={!canEditProfileFields}
                    disableColClass
                    maxLength={STAFF_BIO_LIMITS.volunteersFor}
                    {...register("staffProfile.volunteersFor")}
                  />
                  <CharacterCount name="volunteersFor" value={staffProfileValues?.volunteersFor} />
                </div>
                <div className="col col-12">
                  <TextAreaField
                    label="Org/Chamber Membership"
                    placeholder="Org/Chamber Membership"
                    error={errors.staffProfile?.chamberMembership}
                    validationState={errors.staffProfile?.chamberMembership && "error"}
                    style={{ minHeight: "100px" }}
                    disabled={!canEditProfileFields}
                    disableColClass
                    maxLength={STAFF_BIO_LIMITS.chamberMembership}
                    {...register("staffProfile.chamberMembership")}
                  />
                  <CharacterCount name="chamberMembership" value={staffProfileValues?.chamberMembership} />
                </div>
                <div className="col col-12">
                  <TextAreaField
                    label="Why HLC?"
                    placeholder="Why HLC?"
                    error={errors.staffProfile?.whyHlc}
                    validationState={errors.staffProfile?.whyHlc && "error"}
                    style={{ minHeight: "100px" }}
                    disabled={!canEditProfileFields}
                    disableColClass
                    maxLength={STAFF_BIO_LIMITS.whyHlc}
                    {...register("staffProfile.whyHlc")}
                  />
                  <CharacterCount name="whyHlc" value={staffProfileValues?.whyHlc} />
                </div>
                <div className="col col-12">
                  <TextAreaField
                    label="Hobbies"
                    placeholder="Hobbies"
                    error={errors.staffProfile?.hobbies}
                    validationState={errors.staffProfile?.hobbies && "error"}
                    style={{ minHeight: "100px" }}
                    disabled={!canEditProfileFields}
                    disableColClass
                    maxLength={STAFF_BIO_LIMITS.hobbies}
                    {...register("staffProfile.hobbies")}
                  />
                  <CharacterCount name="hobbies" value={staffProfileValues?.hobbies} />
                </div>
                {showAboutTheOwner && (
                  <div className="col col-12">
                    <TextAreaField
                      label="About the Owner"
                      placeholder="About the Owner"
                      error={errors.staffProfile?.aboutTheOwner}
                      validationState={errors.staffProfile?.aboutTheOwner && "error"}
                      style={{ minHeight: "100px" }}
                      disabled={!canEditProfileFields}
                      disableColClass
                      maxLength={STAFF_BIO_LIMITS.aboutTheOwner}
                      {...register("staffProfile.aboutTheOwner")}
                    />
                    <CharacterCount name="aboutTheOwner" value={staffProfileValues?.aboutTheOwner} />
                  </div>
                )}
              </FormSection>
            </>
          )}
        </div>
      </LoadingOverlay>
    );
  }
);
EditStaffContactForm.displayName = "EditStaffContactForm";

const editContactFormSchema = z
  .object({
    firstName: z.string().trim().min(1, { message: "required" }).max(20, { message: "must be 20 characters or less" }),
    lastName: z.string().trim().min(1, { message: "required" }).max(30, { message: "must be 30 characters or less" }),
    mobilePhone: z.string().trim().refine(isValidOptionalPhoneNumber, { message: "Invalid phone number" }),
    homePhone: z.string().trim().refine(isValidOptionalPhoneNumber, { message: "Invalid phone number" }),
    otherPhone: z.string().trim().refine(isValidOptionalPhoneNumber, { message: "Invalid phone number" }),
    // fake field to hold "must have at least one phone number" error
    phoneNumbers: z.string().optional(),
    personalEmail: z.string().trim().email(),
    line1: z.string().trim(),
    line2: z.string().trim(),
    zipcode: zipcodeSchema.or(z.literal("")),
    city: z.string().trim(),
    state: z.string().trim(),
    emergencyContactName: z.string().trim(),
    emergencyContactPhone: z.string().trim().refine(isValidOptionalPhoneNumber, { message: "Invalid phone number" }),
    employeeNumber: z
      .string()
      .trim()
      .refine(isValidOptionalEmployeeNumber)
      .transform(it => it || null),
    staffProfile: z.object({
      isProfilePublished: z.boolean(),
      graduatedFrom: z.string().trim().max(STAFF_BIO_LIMITS.graduatedFrom),
      degree: z.string().trim().max(STAFF_BIO_LIMITS.degree),
      areaOfStudy: z.string().trim().max(STAFF_BIO_LIMITS.areaOfStudy),
      teachingSince: z.union([
        z.literal(""),
        z
          .string()
          .trim()
          .regex(/^\d+(\.\d+)?$/, { message: "Must be a positive number" })
          .transform(parseFloat)
          .refine(it => it >= 1950 && it <= new Date().getFullYear(), {
            message: `Must be a year between 1950 and ${new Date().getFullYear()}`
          })
          .refine(it => it % 1 === 0, { message: "Must be a valid year" })
      ]),
      certificationsEarned: z.string().trim().max(STAFF_BIO_LIMITS.certificationsEarned),
      volunteersFor: z.string().trim().max(STAFF_BIO_LIMITS.volunteersFor),
      chamberMembership: z.string().trim().max(STAFF_BIO_LIMITS.chamberMembership),
      whyHlc: z.string().trim().max(STAFF_BIO_LIMITS.whyHlc),
      hobbies: z.string().trim().max(STAFF_BIO_LIMITS.hobbies),
      aboutTheOwner: z.string().trim().max(STAFF_BIO_LIMITS.aboutTheOwner)
    })
  })
  .refine(({ mobilePhone, homePhone, otherPhone }) => mobilePhone || homePhone || otherPhone, {
    message: "At least one phone number is required",
    path: ["phoneNumbers"]
  })
  .superRefine(({ staffProfile: { isProfilePublished, graduatedFrom, degree, areaOfStudy, teachingSince } }, ctx) => {
    if (!isProfilePublished) {
      return;
    }
    if (graduatedFrom.length === 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["staffProfile", "graduatedFrom"],
        message: "Required when published"
      });
    }
    if (degree.length === 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["staffProfile", "degree"],
        message: "Required when published"
      });
    }
    if (areaOfStudy.length === 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["staffProfile", "areaOfStudy"],
        message: "Required when published"
      });
    }
    if (teachingSince === "") {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["staffProfile", "teachingSince"],
        message: "Required when published"
      });
    }
  });

type EditContactFormValues = z.infer<typeof editContactFormSchema>;

interface UseEditStaffContactFormModalProps {
  staffMemberId?: string;
}

export const useEditStaffContactFormModal = ({ staffMemberId }: UseEditStaffContactFormModalProps) => {
  const { hasFeatureAccess, activeCenterId } = useAccessControl();
  const staffMemberQuery = useStaffMember({ centerId: activeCenterId, staffMemberId });
  const updateStaffMemberMutator = useUpdateStaffMember(activeCenterId, staffMemberId);
  const employmentHistoryQuery = useEmploymentHistory(activeCenterId, staffMemberId);
  const currentEmployment = employmentHistoryQuery.employmentHistory[0];
  const showAboutTheOwner = currentEmployment?.position === "center_owner" && !currentEmployment.end;

  const formRef = useRef<EditStaffContactFormRef>(null);
  const confirmCloseWithoutSavingModal = useRef<ConfirmHandle>(null);

  const handleBeforeClose = () => {
    if (formRef.current?.isDirty) {
      confirmCloseWithoutSavingModal.current?.confirm(() => {
        hideEditStaffContactModal();
      });
    } else {
      hideEditStaffContactModal();
    }
  };

  const {
    renderModal,
    hide: hideEditStaffContactModal,
    show: showEditStaffContactModal,
    formId
  } = useFormModal({
    name: "edit-staff-contact-form",
    useFormProps: {
      values: {
        ...defaultStaffMemberContactValues,
        ...staffMemberQuery.data,
        employeeNumber: staffMemberQuery.data?.employeeNumber ? `${staffMemberQuery.data?.employeeNumber}` : "",
        staffProfile: {
          ...defaultStaffMemberContactValues.staffProfile,
          ...staffMemberQuery.data?.staffProfile
        }
      },
      resolver: zodResolver(editContactFormSchema),
      mode: "onChange"
    },
    onSubmit: formValues => {
      updateStaffMemberMutator.mutate(
        {
          ...defaultStaffMemberContactValues,
          ...staffMemberQuery.data,
          ...formValues,
          staffProfile: {
            ...defaultStaffMemberContactValues.staffProfile,
            ...staffMemberQuery.data?.staffProfile,
            ...formValues.staffProfile
          }
        },
        { onSuccess: hideEditStaffContactModal }
      );
    },
    onBeforeClose: handleBeforeClose
  });

  const renderEditStaffContactModal = () =>
    renderModal({
      className: "edit-staff-contact-form-modal",
      title: "Edit Staff Contact",
      size: "lg",
      fullscreen: "lg-down",
      body: (
        <EditStaffContactForm
          ref={formRef}
          showAboutTheOwner={showAboutTheOwner}
          showEmployeeNumber={hasFeatureAccess && hasFeatureAccess("employee_id")}
          businessEmail={staffMemberQuery.data?.businessEmail}
          isLoading={updateStaffMemberMutator.isLoading || staffMemberQuery.isLoading}
        />
      ),
      footer: (
        <>
          <Confirm
            ref={confirmCloseWithoutSavingModal}
            title="Confirm"
            message="Would you like to close the modal and lose any unsaved updates?"
          />
          <button className="btn btn-secondary" onClick={handleBeforeClose}>
            Cancel
          </button>
          <LoadingButton
            className="btn btn-primary"
            type="submit"
            form={formId}
            isLoading={updateStaffMemberMutator.isLoading}
            loadingMessage="Saving..."
          >
            Save
          </LoadingButton>
        </>
      )
    });

  return {
    hideEditStaffContactModal,
    showEditStaffContactModal,
    renderEditStaffContactModal
  };
};

const defaultStaffMemberContactValues = {
  id: "",
  firstName: "",
  lastName: "",
  displayName: "",
  businessEmail: "",
  line1: "",
  line2: "",
  city: "",
  state: "",
  zipcode: "",
  mobilePhone: "",
  homePhone: "",
  otherPhone: "",
  personalEmail: "",
  emergencyContactName: "",
  emergencyContactPhone: "",
  isActive: false,
  employeeNumber: "",
  staffProfile: {
    isProfilePublished: false,
    graduatedFrom: "",
    degree: "",
    areaOfStudy: "",
    teachingSince: "",
    certificationsEarned: "",
    volunteersFor: "",
    chamberMembership: "",
    whyHlc: "",
    hobbies: "",
    aboutTheOwner: ""
  } satisfies EditContactFormValues["staffProfile"]
} as const;
